import React, { useEffect, useState } from "react";
import { Table, Button, Form, Spinner, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ModalConfirmar from "../../components/ModalConfirmar";
import PaginacionSimple from "../../components/PaginacionSimple";
import { useAuth } from "../../auth/AuthContext";
import { TOTAL_CUENTA_ID } from "../../components/SelectorCuenta";
import * as gastosEsporadicosApi from "../../api/gastosEsporadicos";
import { formatoNumero } from "../../utils/moneda";

const FORMAS_PAGO = ["Tarjeta", "Bizum", "Transferencia"];
const ICONO_FORMA_PAGO = { Tarjeta: "bi-credit-card", Bizum: "bi-phone", Transferencia: "bi-bank" };

export default function GastosEsporadicos({ cuentaId, cuentas = [], onSaldoCambiado }) {
  const navigate = useNavigate();
  const { esAdministrador } = useAuth();
  const [gastos, setGastos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [monto, setMonto] = useState("");
  const [formaPago, setFormaPago] = useState(FORMAS_PAGO[0]);
  const [guardando, setGuardando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);
  const esTotal = cuentaId === TOTAL_CUENTA_ID;

  const cargar = async (paginaAConsultar = pagina) => {
    if (!cuentaId) return;
    setCargando(true);
    try {
      if (esTotal) {
        const listas = await Promise.all(cuentas.map((c) => gastosEsporadicosApi.listarGastosEsporadicos(c.cuentaId)));
        setGastos(listas.flatMap((r) => r.items).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
        setTotalPaginas(1);
      } else {
        const resultado = await gastosEsporadicosApi.listarGastosEsporadicos(cuentaId, paginaAConsultar);
        setGastos(resultado.items);
        setTotalPaginas(Math.max(1, Math.ceil(resultado.total / resultado.tamanoPagina)));
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { setPagina(1); cargar(1); }, [cuentaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarPagina = (p) => { setPagina(p); cargar(p); };

  const handleAgregar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await gastosEsporadicosApi.registrarGastoEsporadico({ cuentaId, motivo, monto: Number(monto), formaPago });
      setMotivo(""); setMonto(""); setFormaPago(FORMAS_PAGO[0]);
      setPagina(1);
      cargar(1);
      onSaldoCambiado?.();
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    await gastosEsporadicosApi.eliminarGastoEsporadico(aEliminar.gastoEsporadicoId);
    setAEliminar(null);
    cargar();
    onSaldoCambiado?.();
  };

  return (
    <div>
      {!esTotal && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="text-muted small">Se muestran los últimos 2 meses.</div>
          <Button size="sm" variant="outline-secondary" onClick={() => navigate("/historicos")}>
            <i className="bi bi-clock-history me-1"></i>Ver históricos
          </Button>
        </div>
      )}
      {/* Barra rápida: motivo + monto, reduce el saldo automáticamente al guardar */}
      {!esAdministrador && !esTotal && (
      <Form onSubmit={handleAgregar} className="fs-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-sm-5">
            <Form.Label className="small mb-1">Motivo del gasto</Form.Label>
            <Form.Control value={motivo} onChange={(e) => setMotivo(e.target.value)} required placeholder="Ej. Supermercado, taxi, cine..." />
          </div>
          <div className="col-7 col-sm-3">
            <Form.Label className="small mb-1">Monto</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control type="number" step="0.01" min="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} required />
            </InputGroup>
          </div>
          <div className="col-5 col-sm-2">
            <Form.Label className="small mb-1">Forma de pago</Form.Label>
            <Form.Select value={formaPago} onChange={(e) => setFormaPago(e.target.value)} required>
              {FORMAS_PAGO.map((f) => <option key={f} value={f}>{f}</option>)}
            </Form.Select>
          </div>
          <div className="col-12 col-sm-2">
            <Button type="submit" variant="primary" className="w-100" disabled={guardando}>
              {guardando ? <Spinner size="sm" animation="border" /> : "Agregar"}
            </Button>
          </div>
        </div>
      </Form>
      )}

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead>
              <tr><th>Motivo</th><th>Forma de pago</th><th>Fecha</th><th className="text-end">Monto</th><th></th></tr>
            </thead>
            <tbody>
              {gastos.map((g) => (
                <tr key={g.gastoEsporadicoId}>
                  <td data-label="Motivo">{g.motivo}</td>
                  <td data-label="Forma de pago">
                    <i className={`bi ${ICONO_FORMA_PAGO[g.formaPago] || "bi-wallet2"} me-1 text-muted`}></i>
                    {g.formaPago}
                  </td>
                  <td data-label="Fecha">{new Date(g.fecha).toLocaleDateString("es-ES")}</td>
                  <td data-label="Monto" className="text-end text-fs-gasto fw-semibold">${formatoNumero(g.monto)}</td>
                  <td data-label="Acciones" className="text-end">
                    {!esAdministrador && (
                      <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(g)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!gastos.length && (
                <tr><td colSpan={5} className="text-center text-muted py-4">No hay gastos esporádicos registrados.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!esTotal && <PaginacionSimple pagina={pagina} totalPaginas={totalPaginas} onChange={cambiarPagina} />}

      <ModalConfirmar
        show={!!aEliminar}
        titulo="Eliminar gasto"
        mensaje={`¿Eliminar el gasto "${aEliminar?.motivo}"? Esto revertirá el monto al saldo.`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}
